import {
  addHours,
  addDays,
  addMinutes,
  differenceInHours,
  format,
  getHours,
  getMinutes,
  setHours,
  setMilliseconds,
  setMinutes,
  setSeconds,
  startOfHour,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMinutes,
  differenceInMinutes,
  isSameYear,
  isSameMonth,
  isSameDay,
  differenceInSeconds,
  isBefore,
  isAfter,
  endOfHour,
  endOfDay,
  endOfWeek,
  endOfMonth,
  endOfYear,
  startOfMinute,
  endOfMinute,
  roundToNearestMinutes
} from 'date-fns'
import { StartDateMode } from '../enums'
import DateInterval from '../enums/DateInterval'
import { DateRange, Project, ActiveProject } from '../types'
import EndOfDate from '../enums/EndOfDate'

type RoundingMethod = 'ceil' | 'floor' | 'round' | 'trunc'

export function parseDate(date: Date | string | undefined): Date | undefined {
  switch (typeof date) {
    case 'string':
      return date ? new Date(date) : undefined
    case 'object':
      return date instanceof Date ? date : undefined
    default:
      return undefined
  }
}

export function toNearestMinutesInterval(date: Date, interval = 1): Date {
  const minutes: number = Math.floor(getMinutes(date) / interval) * interval
  return setMilliseconds(setSeconds(setMinutes(date, minutes), 0), 0)
}

export function toNearestHoursInterval(date: Date, interval = 1): Date {
  const hours: number = Math.floor(getHours(date) / interval) * interval
  return setMilliseconds(setSeconds(setMinutes(setHours(date, hours), 0), 0), 0)
}

export function roundToNearestFiveMinutes(date: Date, roundingMethod?: RoundingMethod): Date {
  return roundToNearestMinutes(date, { nearestTo: 5, roundingMethod })
}

export function roundToNearestTenMinutes(date: Date, roundingMethod?: RoundingMethod): Date {
  return roundToNearestMinutes(date, { nearestTo: 10, roundingMethod })
}

export function roundToNearestFifteenMinutes(date: Date, roundingMethod?: RoundingMethod): Date {
  return roundToNearestMinutes(date, { nearestTo: 15, roundingMethod })
}

export function humanizeDate(date: Date | string): string {
  if (typeof date === 'string') {
    date = new Date(date)
  }

  return format(date, 'd.M.yyyy')
}

export function humanizeTimestamp(timestamp: Date | string, includeSeconds = false): string {
  if (typeof timestamp === 'string') {
    timestamp = new Date(timestamp)
  }

  return includeSeconds
    ? format(timestamp, 'd.M.yyyy HH:mm:ss')
    : format(timestamp, 'd.M.yyyy HH:mm')
}

export function humanizeDateRange(from: Date | string, to: Date | string): string {
  return `${humanizeTimestamp(from)}–⁠${humanizeTimestamp(to)}`
}

export function humanizeDayRange(from: Date | string, to: Date | string): string {
  if (typeof from === 'string') {
    from = new Date(from)
  }

  if (typeof to === 'string') {
    to = new Date(to)
  }

  if (isSameYear(from, to)) {
    if (isSameMonth(from, to)) {
      return isSameDay(from, to)
        ? `${format(from, 'd.M.yyyy')}`
        : `${format(from, 'd')}.–${format(to, 'd.M.yyyy')}`
    } else {
      return `${format(from, 'd.M')}.–${format(to, 'd.M.yyyy')}`
    }
  } else {
    return `${humanizeDate(from)}–${humanizeDate(to)}`
  }
}

export function getDatesByRange(from: Date, to: Date, interval: DateInterval): Date[] {
  const dates: Date[] = []
  let date: Date = startOfInterval(from, interval)

  while (date <= to) {
    dates.push(date)
    date = addInterval(date, interval)
  }

  return dates
}

export function startOfInterval(date: Date, interval: DateInterval): Date {
  switch (interval) {
    case DateInterval.FIVE_MINUTES:
      return toNearestMinutesInterval(date, 5)
    case DateInterval.TEN_MINUTES:
      return toNearestMinutesInterval(date, 10)
    case DateInterval.FIFTEEN_MINUTES:
      return toNearestMinutesInterval(date, 15)
    case DateInterval.ONE_DAY:
      return subMinutes(startOfDay(date), date.getTimezoneOffset())
    case DateInterval.ONE_HOUR:
    default:
      return startOfHour(date)
  }
}

export function addInterval(date: Date, interval: DateInterval): Date {
  switch (interval) {
    case DateInterval.FIVE_MINUTES:
      return addMinutes(date, 5)
    case DateInterval.TEN_MINUTES:
      return addMinutes(date, 10)
    case DateInterval.FIFTEEN_MINUTES:
      return addMinutes(date, 15)
    case DateInterval.ONE_DAY:
      return addDays(date, 1)
    case DateInterval.ONE_HOUR:
    default:
      return addHours(date, 1)
  }
}

export function getStartDateByMode(
  date: Date,
  mode: StartDateMode,
  project: Project | ActiveProject | null | undefined
): Date {
  switch (mode) {
    case StartDateMode.START_OF_DAY:
      return startOfDay(date)
    case StartDateMode.START_OF_WEEK:
      return startOfWeek(date)
    case StartDateMode.START_OF_MONTH:
      return startOfMonth(date)
    case StartDateMode.START_OF_YEAR:
      return startOfYear(date)
    case StartDateMode.START_OF_PROJECT:
      return startOfDay(project?.startDate ? new Date(project.startDate) : date)
    case StartDateMode.START_OF_SELECTED_DATE_RANGE:
    default:
      return date
  }
}

export function endOf(date: Date, endOfDate: EndOfDate) {
  switch (endOfDate) {
    case EndOfDate.END_OF_HOUR:
      return endOfHour(date)
    case EndOfDate.END_OF_DAY:
      return endOfDay(date)
    case EndOfDate.END_OF_WEEK:
      return endOfWeek(date)
    case EndOfDate.END_OF_MONTH:
      return endOfMonth(date)
    case EndOfDate.END_OF_YEAR:
      return endOfYear(date)
    default:
      return date
  }
}

export function getDifferenceOfDateRangeInHours(
  fromOrDateRange: Date | DateRange,
  to?: Date
): number {
  if ('from' in fromOrDateRange && 'to' in fromOrDateRange) {
    const dateRange = fromOrDateRange
    return Math.abs(differenceInHours(dateRange.from, dateRange.to))
  }

  const from = fromOrDateRange
  return Math.abs(differenceInHours(from as Date, to as Date))
}

/**
 * Get the default date interval for the given date range.
 */
export function getDefaultDateIntervalByDateRange(dateRange: DateRange): DateInterval {
  const hours = getDifferenceOfDateRangeInHours(dateRange)

  if (hours <= 24) {
    return DateInterval.FIVE_MINUTES
  } else if (hours <= 72) {
    return DateInterval.FIFTEEN_MINUTES
  } else if (hours <= 744) {
    return DateInterval.ONE_HOUR
  } else {
    return DateInterval.ONE_DAY
  }
}

/**
 * Check if the given date is more than the given minutes ago.
 *
 * @param date - The date
 * @param minutes - The minutes
 *
 * @returns True if the date is more than the given minutes ago, false otherwise
 */
export function moreThanMinutesAgo(date: Date, minutes: number): boolean {
  return differenceInMinutes(new Date(), date) > minutes
}

/**
 * Check if the given date is less than the given minutes ago.
 *
 * @param date - The date
 * @param minutes - The minutes
 *
 * @returns True if the date is less than the given minutes ago, false otherwise
 */
export function lessThanMinutesAgo(date: Date, minutes: number): boolean {
  return differenceInMinutes(new Date(), date) < minutes
}

/**
 * Check if the given date is more than the given hours ago.
 *
 * @param date - The date
 * @param hours - The hours
 *
 * @returns True if the date is more than the given hours ago, false otherwise
 */
export function moreThanHoursAgo(date: Date, hours: number): boolean {
  return moreThanMinutesAgo(date, hours * 60)
}

/**
 * Check if the given date is more than given days ago.
 *
 * @param date - The date
 * @param days - The days
 *
 * @returns True if the date is more than the given days ago, false otherwise
 */
export function moreThanDaysAgo(date: Date, days: number): boolean {
  return moreThanHoursAgo(date, days * 24)
}

export function isDifferenceLessThanSeconds(first: Date, second: Date, seconds: number): boolean {
  return Math.abs(differenceInSeconds(first, second)) < seconds
}

export function isDifferenceMoreThanSeconds(first: Date, second: Date, seconds: number): boolean {
  return Math.abs(differenceInSeconds(first, second)) > seconds
}

export function isDifferenceLessThanMinutes(first: Date, second: Date, minutes: number): boolean {
  return Math.abs(differenceInMinutes(first, second)) < minutes
}

export function isDifferenceMoreThanMinutes(first: Date, second: Date, minutes: number): boolean {
  return Math.abs(differenceInMinutes(first, second)) > minutes
}

export function isDifferenceLessThanHours(first: Date, second: Date, hours: number): boolean {
  return Math.abs(differenceInHours(first, second)) < hours
}

export function isDifferenceMoreThanHours(first: Date, second: Date, hours: number): boolean {
  return Math.abs(differenceInHours(first, second)) > hours
}

export function isBetween(date: Date, dateRange: DateRange): boolean {
  return isAfter(date, dateRange.from) && isBefore(date, dateRange.to)
}

export function extendStartOfDateRange(
  dateRange: DateRange,
  startDateMode: StartDateMode,
  project?: Project | null
): DateRange {
  const from = getStartDateByMode(dateRange.from, startDateMode, project)
  const to = dateRange.to

  return {
    from,
    to
  }
}

/**
 * Extend the end of the date range.
 */
export function extendEndOfDateRange(dateRange: DateRange, endOfDate: EndOfDate): DateRange {
  const from = dateRange.from
  const to = endOf(dateRange.to, endOfDate)

  return {
    from,
    to
  }
}

export function makeDatesFromDateRange(
  from: Date,
  to: Date,
  interval = DateInterval.ONE_HOUR
): Date[] {
  const dates: Date[] = []

  switch (interval) {
    case DateInterval.ONE_MINUTE:
      from = startOfMinute(from)
      to = endOfMinute(to)
      break

    case DateInterval.FIVE_MINUTES:
      from = roundToNearestFiveMinutes(from, 'floor')
      to = roundToNearestFiveMinutes(to, 'ceil')
      break

    case DateInterval.TEN_MINUTES:
      from = roundToNearestTenMinutes(from, 'floor')
      to = roundToNearestTenMinutes(to, 'ceil')
      break

    case DateInterval.FIFTEEN_MINUTES:
      from = roundToNearestFifteenMinutes(from, 'floor')
      to = roundToNearestFifteenMinutes(to, 'ceil')
      break

    case DateInterval.ONE_HOUR:
      from = startOfHour(from)
      to = endOfHour(to)
      break

    case DateInterval.ONE_DAY:
      from = startOfDay(from)
      to = endOfDay(to)
      break
  }

  let date = from

  while (date <= to) {
    dates.push(date)

    switch (interval) {
      case DateInterval.ONE_MINUTE:
        date = addMinutes(date, 1)
        break

      case DateInterval.FIVE_MINUTES:
        date = addMinutes(date, 5)
        break

      case DateInterval.TEN_MINUTES:
        date = addMinutes(date, 10)
        break

      case DateInterval.FIFTEEN_MINUTES:
        date = addMinutes(date, 15)
        break

      case DateInterval.ONE_HOUR:
        date = addHours(date, 1)
        break

      case DateInterval.ONE_DAY:
        date = addDays(date, 1)
        break
    }
  }

  return dates
}

export function formatISO(date: Date): string {
  return date.toISOString().split('.')[0] + 'Z'
}
